import { Box, Typography, Stack, Chip, Paper, Link, Tooltip } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import ApiIcon from '@mui/icons-material/Api';
import StorageIcon from '@mui/icons-material/Storage';
import BuildIcon from '@mui/icons-material/Build';
import ScienceIcon from '@mui/icons-material/Science';

// #107 — 제품이 실제로 서비스되는 도메인. SSOT: products.yaml 의 hosts[].
//
// 🔴 type 을 지우고 호스트만 나열하면 집계가 틀린다.
//   api.raymedical.com 은 EMS 장비 호출로 요청 수 1위지만 프런트엔드가 없다.
//   www.raymedical.com 과 같은 표에 "사용량" 으로 놓으면 홈페이지가 안 보인다.
//   그래서 성격을 값 옆에 붙여 읽는 사람이 오해할 여지를 줄인다.
//
// 🔴 비어 있어도 섹션을 숨기지 않는다. 숨기면 "도메인이 없는 제품" 과
//   "아직 매핑하지 않은 제품" 이 구별되지 않는다.

const TYPE_META = {
  web: { label: '웹', icon: <LanguageIcon />, color: 'success', hint: '사람이 브라우저로 쓰는 화면' },
  api: { label: 'API', icon: <ApiIcon />, color: 'info', hint: '기계가 호출하는 엔드포인트 — 프런트엔드 없음', note: '프런트엔드 없음' },
  infra: { label: '기반', icon: <StorageIcon />, color: 'default', hint: '저장소 · 게이트웨이 등 내부 기반' },
  internal: { label: '사내', icon: <BuildIcon />, color: 'default', hint: '사내 도구' },
  env: { label: '개발·테스트', icon: <ScienceIcon />, color: 'warning', hint: '개발 · 스테이징 · 테스트 — 운영 집계에서 제외', note: '운영 집계 제외' },
};

const ORDER = ['web', 'api', 'infra', 'internal', 'env'];

export default function ServiceHostsList({ hosts }) {
  const list = Array.isArray(hosts) ? hosts : [];

  if (list.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderStyle: 'dashed' }}>
        <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
          등록된 서비스 도메인이 없습니다.
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: 'text.disabled', mt: 0.5 }}>
          도메인이 없는 제품일 수도, 아직 매핑하지 않은 것일 수도 있습니다 — 이 화면은 둘을 구별하지
          못합니다. 확인되면 <code>products.yaml</code> 의 <code>hosts:</code> 에 추가하세요
          (추측해서 채우지 않습니다).
        </Typography>
      </Paper>
    );
  }

  const sorted = [...list].sort(
    (a, b) => ORDER.indexOf(a.type || 'web') - ORDER.indexOf(b.type || 'web'),
  );

  return (
    <Stack spacing={1}>
      {sorted.map((h) => {
        const meta = TYPE_META[h.type] || { label: h.type || '미분류', color: 'default', hint: '성격 미지정' };
        const linkable = h.type === 'web' || h.type === 'internal';
        return (
          <Paper key={h.host} variant="outlined" sx={{ px: 1.75, py: 1.25 }}>
            <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
              <Tooltip title={meta.hint}>
                <Chip
                  size="small"
                  icon={meta.icon}
                  label={meta.label}
                  color={meta.color}
                  variant={h.type === 'env' ? 'outlined' : 'filled'}
                  sx={{ height: 22, fontSize: 11, fontWeight: 700 }}
                />
              </Tooltip>
              {linkable ? (
                <Link
                  href={`https://${h.host}`}
                  target="_blank"
                  rel="noopener"
                  sx={{ fontFamily: 'monospace', fontSize: 13.5, fontWeight: 600 }}
                >
                  {h.host}
                </Link>
              ) : (
                <Typography sx={{ fontFamily: 'monospace', fontSize: 13.5, fontWeight: 600 }}>
                  {h.host}
                </Typography>
              )}
              <Box sx={{ flex: 1 }} />
              {meta.note && (
                <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>{meta.note}</Typography>
              )}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}
